import { type CarePlanItemKind, type TaskCategoryCode, TASK_CATEGORY_CODES } from './types';

/**
 * Render-agnostic Day-view ring taxonomy: which care-plan segment a task category maps to,
 * the segment colors, and progress folding. Shared by patient-web (`@liza/ui`) and
 * patient-native so both clients color and order categories identically. The renderer-specific
 * bits (the web CSS gradient, the native lucide icon map) stay in their per-renderer layers.
 */

/**
 * The day-view progress-ring segments, one per care-plan item kind: Results (measurements),
 * Medications, Lifestyle (all four lifestyle pillars combined), and Tasks. Tasks have no section
 * color, so they read as a neutral grayscale segment.
 */
export type RingCategory = 'measurement' | 'medication' | 'lifestyle' | 'task';

/** Segment order shown in the ring + legend — follows the care-plan section order. */
export const RING_CATEGORIES: readonly RingCategory[] = ['measurement', 'medication', 'lifestyle', 'task'];

/** Display label per ring segment, using the care plan's terminology. */
export const RING_CATEGORY_LABELS: Record<RingCategory, string> = {
  measurement: 'Results',
  medication: 'Medications',
  lifestyle: 'Lifestyle',
  task: 'Tasks',
};

/** Ring colors. Single source of truth shared by the ring segments and the item icons. */
export const RING_CATEGORY_COLORS: Record<RingCategory, string> = {
  measurement: '#9B6FD4',
  medication: '#4DB8CC',
  lifestyle: '#5CB88A',
  task: '#71717A',
};

/**
 * Maps a task category to the ring segment it contributes to. Measurements and medications map
 * 1:1; the lifestyle pillars (movement/food/sleep/mind/other) fold into `lifestyle`; `clinical`
 * (generic care-team tasks) folds into the grayscale `task` segment. Uncategorized tasks have no
 * category and return `undefined`.
 */
const CATEGORY_TO_RING: Partial<Record<TaskCategoryCode, RingCategory>> = {
  measurement: 'measurement',
  medication: 'medication',
  movement: 'lifestyle',
  food: 'lifestyle',
  sleep: 'lifestyle',
  mind: 'lifestyle',
  other: 'lifestyle',
  clinical: 'task',
};

/** The ring segment a task category belongs to, or `undefined` when it isn't a ring segment. */
export function getRingCategory(category: TaskCategoryCode): RingCategory | undefined {
  return CATEGORY_TO_RING[category];
}

/** Hex color for a task category, or `undefined` when the category isn't a ring segment. */
export function getTaskCategoryColor(category: TaskCategoryCode | null | undefined): string | undefined {
  const ring = category ? CATEGORY_TO_RING[category] : undefined;
  return ring ? RING_CATEGORY_COLORS[ring] : undefined;
}

/** Hex color for a care-plan item kind (used by the "Add to plan" menu). Each kind is a ring segment. */
export function getItemKindColor(kind: CarePlanItemKind): string {
  return RING_CATEGORY_COLORS[kind];
}

export type CategoryProgress = { percentage: number; total: number };

/**
 * Fold per-category progress (the seven task categories) into the ring segments, summing each
 * segment's items. The source gives percentages rather than raw counts, so we reconstruct an
 * approximate completed count per category to re-derive each segment's percentage. All current
 * categories map to a segment (clinical folds into `task`); the guard skips any future
 * `TASK_CATEGORY_CODES` entry added without a `CATEGORY_TO_RING` mapping.
 */
export function foldProgressToRingSegments(
  progress: Record<TaskCategoryCode, CategoryProgress>,
): Record<RingCategory, CategoryProgress> {
  const acc = Object.fromEntries(RING_CATEGORIES.map((c) => [c, { completed: 0, total: 0 }])) as Record<
    RingCategory,
    { completed: number; total: number }
  >;
  for (const category of TASK_CATEGORY_CODES) {
    const segment = CATEGORY_TO_RING[category];
    if (!segment) continue;
    const { percentage, total } = progress[category];
    acc[segment].total += total;
    acc[segment].completed += Math.round((percentage / 100) * total);
  }
  return Object.fromEntries(
    RING_CATEGORIES.map((c) => [
      c,
      { percentage: acc[c].total > 0 ? (acc[c].completed / acc[c].total) * 100 : 0, total: acc[c].total },
    ]),
  ) as Record<RingCategory, CategoryProgress>;
}
